import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, AlertTitle, Box, LinearProgress, Portal, Snackbar, Stack } from '@mui/material';

/**
 * Global toast notification system.
 *
 * - <ToastProvider /> mounted once in App.js renders stacked snackbars
 *   bottom-right, visible on every module/page.
 * - useToast() hook for components.
 * - `toast` singleton for non-React code (axios interceptors, services)
 *   so background API processes can report pending/success/error.
 *
 * Supported severities: 'pending' | 'success' | 'error' | 'info' | 'warning'
 * A 'pending' toast shows a progress bar and stays open until updated or
 * dismissed via its id (toast.update(id, ...)).
 */

const ToastContext = createContext(null);

let toastId = 0;
const listeners = new Set();
const queue = []; // toasts fired before the provider mounted

const emit = (action) => {
  if (listeners.size === 0) {
    if (action.type === 'add' || action.type === 'update') queue.push(action);
    return;
  }
  listeners.forEach((fn) => fn(action));
};

const addToast = ({ message, title, severity = 'info', duration, id }) => {
  const toastItem = {
    id: id ?? `toast-${++toastId}-${Date.now()}`,
    message,
    title,
    severity,
    duration: duration ?? (severity === 'pending' ? 0 : 5000),
    createdAt: Date.now(),
  };
  emit({ type: 'add', toast: toastItem });
  return toastItem.id;
};

/**
 * Imperative toast API — safe to call anywhere (components, services,
 * interceptors). Example:
 *   const id = toast.pending('Menghapus data...');
 *   toast.update(id, { severity: 'success', message: 'Data dihapus' });
 */
export const toast = {
  show: (message, options = {}) => addToast({ message, ...options }),
  success: (message, options = {}) => addToast({ message, severity: 'success', ...options }),
  error: (message, options = {}) => addToast({ message, severity: 'error', duration: 7000, ...options }),
  info: (message, options = {}) => addToast({ message, severity: 'info', ...options }),
  warning: (message, options = {}) => addToast({ message, severity: 'warning', ...options }),
  pending: (message, options = {}) => addToast({ message, severity: 'pending', duration: 0, ...options }),
  update: (id, patch) => emit({ type: 'update', id, patch }),
  dismiss: (id) => emit({ type: 'dismiss', id }),
  clear: () => emit({ type: 'clear' }),
};

const severityToAlert = { pending: 'info', success: 'success', error: 'error', info: 'info', warning: 'warning' };

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const apply = (action) => {
      setToasts((prev) => {
        switch (action.type) {
          case 'add': {
            if (prev.some((t) => t.id === action.toast.id)) return prev;
            return [...prev, action.toast].slice(-5); // keep max 5 visible
          }
          case 'update':
            return prev.map((t) => (t.id === action.id ? { ...t, ...action.patch } : t));
          case 'dismiss':
            return prev.filter((t) => t.id !== action.id);
          case 'clear':
            return [];
          default:
            return prev;
        }
      });
    };
    listeners.add(apply);
    // flush toasts fired before mount
    queue.splice(0).forEach(apply);
    return () => listeners.delete(apply);
  }, []);

  const dismiss = useCallback((id) => toast.dismiss(id), []);

  const value = useMemo(
    () => ({
      toast,
      toasts,
      dismiss,
    }),
    [toasts, dismiss]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Portal>
        <Stack
          spacing={1}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: (theme) => theme.zIndex.snackbar + 10,
            maxWidth: 380,
            width: '100%',
          }}
        >
          {toasts.map((t) => (
            <Snackbar
              key={t.id}
              open
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              autoHideDuration={t.duration > 0 ? t.duration : null}
              onClose={(_, reason) => {
                if (reason === 'clickaway') return;
                dismiss(t.id);
              }}
              sx={{ position: 'static', transform: 'none' }}
            >
              <Box>
                <Alert
                  variant="filled"
                  severity={severityToAlert[t.severity] || 'info'}
                  onClose={() => dismiss(t.id)}
                  sx={{ width: '100%', boxShadow: 4 }}
                >
                  {t.title ? <AlertTitle>{t.title}</AlertTitle> : null}
                  {t.message}
                </Alert>
                {t.severity === 'pending' && <LinearProgress sx={{ mt: 0.5, borderRadius: 1 }} />}
              </Box>
            </Snackbar>
          ))}
        </Stack>
      </Portal>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) return { toast, toasts: [], dismiss: toast.dismiss }; // safe fallback outside provider
  return ctx;
};

export default ToastContext;
